import React, { useCallback, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  IconButton,
} from "@mui/material";
import { Round, ROUND_STATUS, RoundSolve } from "./utils/MatchUtils";
import { formatTime } from "../hooks/useSolve";
import TwistyPlayerComponent from "./TwistyPlayerComponent";
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CircularProgress from '@mui/material/CircularProgress';
import VerifiedIcon from '@mui/icons-material/Verified';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';

export interface BasicTableProps {
  title?: string,
  roundNumber: number,
  solveNumber: number,
  solveResults: RoundSolve[],
  rounds: Round[],
  updateStatus: (roundNumber: number, solveNumber: number, scramble: string, time: number | null, status: ROUND_STATUS) => void
}

const BasicTable: React.FC<BasicTableProps> = ({ title, solveResults, rounds, roundNumber, solveNumber, updateStatus }) => {
  const [editingSolve, setEditingSolve] = useState<RoundSolve | null>(null);

  const [viewScramble, setViewScramble] = useState<{ roundNumber: number, solveNumber: number, scramble: string } | null>(null);

  const handleEditSolve = (solve: RoundSolve) => {
    setEditingSolve(solve);
  };

  const handleSave = () => {
    if (editingSolve) {
      //onUpdateSolve?.(editingSolve);
      updateStatus(editingSolve.roundNumber, editingSolve.solveNumber, editingSolve.scramble, editingSolve.solveTime, editingSolve.status)
      setEditingSolve(null); // Close modal
    }
  };

  const handleClose = () => {
    setEditingSolve(null); // Close modal without saving
  };

  const getButtonNameFromStatus = useCallback((roundNumber: number, solveNumber: number) => {
    const round = rounds[roundNumber] //.find((r) => r === roundNumber);

    // If the round doesn't exist or the scramble isn't provided for the solve, it's Pending
    if (!round || !round.scrambles[solveNumber]) {
      return "Reveal";
    }

    // Find the solve result for the given round and solve number
    const solve = solveResults.find(
      (s) => s.roundNumber === roundNumber && s.solveNumber === solveNumber
    );

    // If the solve result doesn't exist, it's Pending
    if (!solve) {
      return "Reveal"
    }

    // If the solve exists but doesn't have a time, it's Waiting
    switch (solve.status) {
      case ROUND_STATUS.Revealed:
      case ROUND_STATUS.Scrambling:
        return "Waiting...";
      case ROUND_STATUS.Scrambled:
        return "Verify Scramble";
      case ROUND_STATUS.ScrambleVerified:
        return "Start";
      case ROUND_STATUS.ReadyToSolve:
        return "Waiting...";
      case ROUND_STATUS.Solving:
        return "Waiting...";
      case ROUND_STATUS.Solved:
        return "Verify Solve";
      case ROUND_STATUS.SolveVerified:
        return "";
    }

  }, [solveResults])

  const getStatus = useCallback((roundNumber: number, solveNumber: number) => {
    const round = rounds[roundNumber] //.find((r) => r === roundNumber);

    // If the round doesn't exist or the scramble isn't provided for the solve, it's Pending
    if (!round || !round.scrambles[solveNumber]) {
      return "HIDDEN";
    }

    // Find the solve result for the given round and solve number
    const solve = solveResults.find(
      (s) => s.roundNumber === roundNumber && s.solveNumber === solveNumber
    );

    // If the solve result doesn't exist, it's Pending
    if (!solve) {
      return "HIDDEN"
    }

    return solve.status.toUpperCase()

  }, [solveResults])

  const getSolveTime = useCallback((roundNumber: number, solveNumber: number) => {
    const round = rounds[roundNumber] //.find((r) => r === roundNumber);

    // If the round doesn't exist or the scramble isn't provided for the solve, it's Pending
    if (!round || !round.scrambles[solveNumber]) {
      return "--";
    }

    // Find the solve result for the given round and solve number
    const solve = solveResults.find(
      (s) => s.roundNumber === roundNumber && s.solveNumber === solveNumber
    );

    // If the solve result doesn't exist, it's Pending
    if (!solve) {
      return "--"
    }

    if (solve.solveTime === null) {
      return "--"
    }

    return formatTime(solve.solveTime);

  }, [solveResults])

  const getSolve = useCallback((roundNumber: number, solveNumber: number) => {
    const round = rounds[roundNumber] //.find((r) => r === roundNumber);

    // If the round doesn't exist or the scramble isn't provided for the solve, it's Pending
    if (!round || !round.scrambles[solveNumber]) {
      return null;
    }

    // Find the solve result for the given round and solve number
    const solve = solveResults.find(
      (s) => s.roundNumber === roundNumber && s.solveNumber === solveNumber
    );

    // If the solve result doesn't exist, it's Pending
    if (!solve) {
      return null
    }

    // if (solve.solveTime === null) {
    //   return null
    // }

    return solve;

  }, [solveResults])


  return (
    <>
      <TableContainer component={Paper}>
        {title && (
          <Typography
            variant="h6"
            sx={{ textAlign: "center", margin: "1em 0" }}
          >
            {title}
          </Typography>
        )}
        {rounds.map((result, index) => (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Solve</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Time (s)</TableCell>
                <TableCell>Scramble</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.scrambles.filter((_s, i) => i < 5).map((scramble, idx) => {
                const status = getButtonNameFromStatus(index, idx)
                const currentRoundAndSolve = roundNumber === index && solveNumber === idx;
                const solve = getSolve(index, idx)
                return (
                  <TableRow key={`${index}-${idx}`} selected={currentRoundAndSolve}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell><Chip size="small" label={getStatus(index, idx)} /></TableCell>
                    <TableCell>{getSolveTime(index, idx)}</TableCell>
                    <TableCell>
                      <Button variant="text" size="small" onClick={() => {
                        if(solve !==null && scramble!==solve.scramble){
                          setViewScramble({ roundNumber: index, solveNumber: idx, scramble: solve.scramble })
                        } else {
                          setViewScramble({ roundNumber: index, solveNumber: idx, scramble: scramble })
                        }
                      }}>
                        {solve !==null && scramble!==solve.scramble ? "*** " + solve.scramble :scramble}
                      </Button>

                    </TableCell>
                    <TableCell align="right">

                      {status === "Waiting..." && <IconButton aria-label="VisibilityIcon" disabled size="small">
                        <CircularProgress size={18} color="warning" />
                      </IconButton>}

                      {currentRoundAndSolve && status === "Reveal" && <IconButton aria-label="VisibilityIcon" color="success" size="small" onClick={() => {
                        updateStatus(index, idx, scramble, null, ROUND_STATUS.Revealed)
                      }}>
                        <VisibilityIcon fontSize="inherit" />
                      </IconButton>}

                      {status === "Verify Scramble" && <IconButton aria-label="ThumbUpAltIcon" color="success" size="small" onClick={() => {
                        updateStatus(index, idx, scramble, null, ROUND_STATUS.ScrambleVerified)
                      }}>
                        <ThumbUpAltIcon fontSize="inherit" />
                      </IconButton>}

                      {status === "Start" && <IconButton aria-label="PlayCircleFilledIcon" color="success" size="small" onClick={() => {
                        updateStatus(index, idx, scramble, null, ROUND_STATUS.ReadyToSolve)
                      }}>
                        <PlayCircleFilledIcon fontSize="inherit" />
                      </IconButton>}

                      {status === "Verify Solve" && <IconButton aria-label="VerifiedIcon" color="success" size="small" onClick={() => {
                        const solve2 = getSolve(index, idx)
                        if (solve2 !== null) {
                          updateStatus(index, idx, scramble, solve2.solveTime, ROUND_STATUS.SolveVerified)
                        }
                      }}>
                        <VerifiedIcon fontSize="inherit" />
                      </IconButton>}

                      {solve !== null && <IconButton aria-label="EditNoteIcon" size="small" onClick={() => {
                        if (solve !== null) {
                          handleEditSolve(solve)
                        }
                      }}>
                        <EditNoteIcon fontSize="inherit" />
                      </IconButton>}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        ))}
      </TableContainer>

      {editingSolve && (
        <Dialog open={!!editingSolve} onClose={handleClose}>
          <DialogTitle>Edit Solve</DialogTitle>
          <DialogContent>
            <Typography variant="subtitle1" color={"GrayText"} sx={{ mb: 2, mt: 1 }}>
              {`Round: ${editingSolve.roundNumber + 1}, Solve: ${editingSolve.solveNumber + 1}`}
            </Typography>
            <TextField
              label="Scramble"
              size="small"
              value={editingSolve.scramble}
              fullWidth
              onChange={(e) =>
                setEditingSolve({
                  ...editingSolve,
                  scramble: e.target.value,
                })
              }
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                label="Status"
                value={editingSolve.status}
                onChange={(e) =>
                  setEditingSolve({
                    ...editingSolve,
                    status: e.target.value as ROUND_STATUS,
                    solveTime: e.target.value != "Solve Verified" ? null : editingSolve.solveTime
                  })
                }
              >
                {Object.values(ROUND_STATUS).map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Time (ms)"
              size="small"
              type="number"
              value={editingSolve.solveTime || ""}
              onChange={(e) =>
                setEditingSolve({
                  ...editingSolve,
                  solveTime: parseFloat(e.target.value) || 0,
                })
              }
              fullWidth
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="secondary">
              Cancel
            </Button>
            <Button onClick={handleSave} color="primary" variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {viewScramble !== null && (
        <Dialog maxWidth="sm" fullWidth open={!!viewScramble} onClose={() => setViewScramble(null)}>
          <DialogTitle>{`Round: ${viewScramble.roundNumber + 1}, Solve: ${viewScramble.solveNumber + 1}`}</DialogTitle>
          <DialogContent>
            <Typography variant="h6">{viewScramble.scramble}</Typography>
            <TwistyPlayerComponent
              width={"550px"}
              height={"400px"}
              alg={viewScramble.scramble}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewScramble(null)} color="primary" variant="contained">
              OK
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default BasicTable;
